import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import { Viewport, ViewSmall, ViewLarge } from '@shared-ui/viewport-context';
import * as React from 'react';
import { UitkFakeInput, UitkTextInput } from 'uitk-react-input';
import { classNames, isElementVisible, liveAnnounce, analyticsTracker } from 'uitk-react-utils';
import { ESSAdapter } from './adapters/ess-adapter.esm.js';
import { UitkTypeaheadDialog } from './uitk-typeahead-dialog.esm.js';
import { UitkTypeaheadInput } from './uitk-typeahead-input.esm.js';
import { UitkTypeaheadMenu } from './uitk-typeahead-menu.esm.js';
import { UitkTypeaheadSelections } from './uitk-typeahead-selections.esm.js';
import styles from './uitk-typeahead.css';

var getClassNameList = classNames.bind(styles);
var l10nStrings = {
    enteringInput: 'typeahead.enteringInput'
};
var TypeaheadRenderType;
(function (TypeaheadRenderType) {
    TypeaheadRenderType["MENU"] = "menu";
    TypeaheadRenderType["DIALOG"] = "dialog";
    TypeaheadRenderType["ADAPTIVE"] = "adaptive";
})(TypeaheadRenderType || (TypeaheadRenderType = {}));
var Typeahead = /** @class */ (function (_super) {
    __extends(Typeahead, _super);
    function Typeahead(props) {
        var _this = _super.call(this, props) || this;
        _this.keyArray = [];
        _this.state = {
            dialog: false,
            mounted: false,
            searchTerm: '',
            startSearchTerm: ''
        };
        if (props.isMulti && !Array.isArray(props.value)) {
            console.warn('Use an array of values if set to isMulti');
        }
        _this.typeaheadInputRef = React.createRef();
        _this.handleResultItemSelect = _this.handleResultItemSelect.bind(_this);
        _this.dismissTypeahead = _this.dismissTypeahead.bind(_this);
        _this.openTypeahead = _this.openTypeahead.bind(_this);
        _this.renderDialog = _this.renderDialog.bind(_this);
        _this.renderMenu = _this.renderMenu.bind(_this);
        _this.handleClear = _this.handleClear.bind(_this);
        _this.handleKeyPress = _this.handleKeyPress.bind(_this);
        _this.focusTypeaheadInput = _this.focusTypeaheadInput.bind(_this);
        _this.renderMenuTypeahead = _this.renderMenuTypeahead.bind(_this);
        _this.renderDialogTypeahead = _this.renderDialogTypeahead.bind(_this);
        _this.updateStartSearchTerm = _this.updateStartSearchTerm.bind(_this);
        return _this;
    }
    Typeahead.getDerivedStateFromProps = function (nextProps, prevState) {
        var _a;
        if (nextProps.isMulti) {
            return __assign(__assign({}, prevState), { searchTerm: '' });
        }
        if (nextProps.value !== prevState.searchTerm) {
            return __assign(__assign({}, prevState), { searchTerm: (_a = nextProps.value) !== null && _a !== void 0 ? _a : '' });
        }
        return null;
    };
    Typeahead.prototype.componentDidMount = function () {
        var _a = this.props, value = _a.value, onMountCB = _a.onMountCB, isMulti = _a.isMulti;
        if (Array.isArray(value) && isMulti) {
            this.setState({ searchTerm: value.join(', ') });
        }
        this.setState({ mounted: true });
        if (onMountCB) {
            onMountCB(this.typeaheadInputRef);
        }
    };
    Typeahead.prototype.handleKeyPress = function (e, triggerType) {
        var code = e.keyCode || e.which;
        var keyValue = e.key;
        if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
            this.keyArray.push(keyValue);
            var joinedKeyArray = this.keyArray.join('');
            if (triggerType === TypeaheadRenderType.DIALOG) {
                this.updateStartSearchTerm(triggerType, joinedKeyArray);
            }
        }
    };
    Typeahead.prototype.handleClear = function () {
        var handleClear = this.props.handleClear;
        this.setState({
            searchTerm: ''
        });
        if (typeof handleClear === 'function') {
            handleClear();
        }
    };
    Typeahead.prototype.handleResultItemSelect = function (term, data) {
        var _a;
        var _b = this.props, onUpdate = _b.onUpdate, isMulti = _b.isMulti, onResultSelection = _b.onResultSelection, id = _b.id;
        if (onUpdate) {
            onUpdate({ term: term, data: data });
        }
        if (onResultSelection) {
            var event_1 = new CustomEvent('change');
            var input = document.createElement('input');
            input.value = term;
            if ((data && data.id) || id) {
                input.setAttribute('id', (_a = data.id) !== null && _a !== void 0 ? _a : id);
            }
            input.addEventListener('change', function (inputEvent) {
                onResultSelection(inputEvent);
            });
            input.dispatchEvent(event_1);
        }
        if (!isMulti) {
            this.setState({
                searchTerm: term
            });
            this.focusTypeaheadInput();
        }
    };
    Typeahead.prototype.getAdapter = function () {
        var _a = this.props, adapter = _a.adapter, l10n = _a.l10n;
        if (adapter instanceof ESSAdapter) {
            adapter.setL10n(l10n);
        }
        return adapter;
    };
    Typeahead.prototype.dismissTypeahead = function (triggerType) {
        var onDismiss = this.props.onDismiss;
        var isVisible = isElementVisible(this.typeaheadInputRef.current);
        if (typeof onDismiss === 'function') {
            onDismiss(triggerType);
        }
        this.setState({ dialog: false, startSearchTerm: '' });
        this.keyArray = [];
        if (this.typeaheadInputRef.current && isVisible) {
            this.typeaheadInputRef.current.focus();
        }
    };
    Typeahead.prototype.updateStartSearchTerm = function (triggerType, keyValue) {
        var _this = this;
        this.setState({ startSearchTerm: keyValue }, function () {
            if (triggerType === 'dialog') {
                _this.openTypeahead(triggerType);
            }
        });
    };
    Typeahead.prototype.openTypeahead = function (triggerType) {
        var _a = this.props, adapter = _a.adapter, onClick = _a.onClick;
        var _b = this.state, dialog = _b.dialog, searchTerm = _b.searchTerm;
        var previousState = dialog;
        // To handle menu open/close
        if (triggerType === TypeaheadRenderType.MENU) {
            if (previousState) {
                this.dismissTypeahead(triggerType);
            }
            else {
                if (typeof onClick === 'function') {
                    onClick(triggerType);
                }
                this.setState({ dialog: true });
                setTimeout(function () {
                    adapter.typeaheadDialogOpened(searchTerm);
                });
            }
        }
        // To handle dialog open/close
        else if (triggerType === TypeaheadRenderType.DIALOG) {
            if (typeof onClick === 'function') {
                onClick(triggerType);
            }
            this.setState({ dialog: true });
        }
    };
    Typeahead.prototype.focusTypeaheadInput = function () {
        var l10n = this.props.l10n;
        liveAnnounce(l10n.formatText(l10nStrings.enteringInput));
        if (this.typeaheadInputRef.current) {
            this.typeaheadInputRef.current.focus();
        }
    };
    Typeahead.prototype.renderMenu = function () {
        var _this = this;
        var _a = this.props, allowExactMatch = _a.allowExactMatch, label = _a.label, listTitle = _a.listTitle, categorize = _a.categorize, onInputChange = _a.onInputChange, stid = _a.stid, typeaheadPlaceholder = _a.typeaheadPlaceholder, typeaheadEmptyResultPlaceholder = _a.typeaheadEmptyResultPlaceholder, value = _a.value, _b = _a.isMulti, isMulti = _b === void 0 ? false : _b, customTrigger = _a.customTrigger, dismissButtonText = _a.dismissButtonText, clearAllText = _a.clearAllText, handleClear = _a.handleClear, onItemRemove = _a.onItemRemove, selectedItemHeading = _a.selectedItemHeading, l10n = _a.l10n, defaultTerm = _a.defaultTerm, debounceRate = _a.debounceRate, required = _a.required;
        var additionalProps = {
            onDismiss: function () {
                _this.dismissTypeahead('menu');
            }
        };
        var _c = this.state, dialog = _c.dialog, searchTerm = _c.searchTerm;
        var adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        return (React.createElement(UitkTypeaheadMenu, __assign({}, additionalProps, { isMulti: isMulti, value: value, label: label, adapter: adapter, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, allowExactMatch: allowExactMatch, onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, stid: stid, term: searchTerm !== null && searchTerm !== void 0 ? searchTerm : defaultTerm, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear, clearAllText: clearAllText, onClearAll: handleClear, dismissButtonText: dismissButtonText !== null && dismissButtonText !== void 0 ? dismissButtonText : l10n.formatText('typeahead.multiselect.dismiss'), onItemRemove: onItemRemove, selectedItemHeading: selectedItemHeading, defaultTerm: defaultTerm, debounceRate: debounceRate, typeaheadInputRef: this.typeaheadInputRef, historyDetail: adapter.isHistoryDetailEnabled(), required: required })));
    };
    Typeahead.prototype.renderDialog = function () {
        var _this = this;
        var _a = this.props, allowExactMatch = _a.allowExactMatch, id = _a.id, label = _a.label, listTitle = _a.listTitle, categorize = _a.categorize, onInputChange = _a.onInputChange, stid = _a.stid, typeaheadPlaceholder = _a.typeaheadPlaceholder, typeaheadEmptyResultPlaceholder = _a.typeaheadEmptyResultPlaceholder, customTrigger = _a.customTrigger, defaultTerm = _a.defaultTerm, _b = _a.isMulti, isMulti = _b === void 0 ? false : _b, clearAllText = _a.clearAllText, handleClear = _a.handleClear, dismissButtonText = _a.dismissButtonText, selectedItemHeading = _a.selectedItemHeading, onItemRemove = _a.onItemRemove, value = _a.value, l10n = _a.l10n, debounceRate = _a.debounceRate, strictFullscreen = _a.strictFullscreen, required = _a.required;
        var _c = this.state, dialog = _c.dialog, searchTerm = _c.searchTerm, startSearchTerm = _c.startSearchTerm;
        var adapter = this.getAdapter();
        if (categorize) {
            adapter.setTypeaheadCategories();
        }
        var additionalProps = {
            onDismiss: function () {
                _this.dismissTypeahead(TypeaheadRenderType.DIALOG);
            }
        };
        return (React.createElement(UitkTypeaheadDialog, __assign({ adapter: adapter, allowExactMatch: allowExactMatch, dialog: (customTrigger && customTrigger.showTypeahead) || dialog, id: id, label: label }, additionalProps, { onInputChange: onInputChange, onResultItemSelect: this.handleResultItemSelect, term: searchTerm, startSearchTerm: startSearchTerm, stid: stid, placeholder: typeaheadPlaceholder, emptyResultPlaceholder: typeaheadEmptyResultPlaceholder, listTitle: listTitle, categorize: categorize, handleClear: this.handleClear, defaultTerm: defaultTerm, isMulti: isMulti, clearAllText: clearAllText, onClearAll: handleClear, dismissButtonText: dismissButtonText !== null && dismissButtonText !== void 0 ? dismissButtonText : l10n.formatText('typeahead.multiselect.dismiss'), onItemRemove: onItemRemove, selectedItemHeading: selectedItemHeading, value: value, debounceRate: debounceRate, strictFullscreen: strictFullscreen, typeaheadInputRef: this.typeaheadInputRef, historyDetail: adapter.isHistoryDetailEnabled(), required: required })));
    };
    Typeahead.prototype.renderTrigger = function (triggerType) {
        var _this = this;
        var _a = this.props, accessibilityLabel = _a.accessibilityLabel, analytics = _a.analytics, icon = _a.icon, hasClearButton = _a.hasClearButton, hasVisibleLabel = _a.hasVisibleLabel, invalid = _a.invalid, label = _a.label, name = _a.name, placeholder = _a.placeholder, stid = _a.stid, customTrigger = _a.customTrigger, children = _a.children, isMulti = _a.isMulti, value = _a.value, required = _a.required;
        var _b = this.state, mounted = _b.mounted, searchTerm = _b.searchTerm, dialog = _b.dialog;
        var a11yLabel = accessibilityLabel || label;
        var TriggerTag = triggerType === 'menu' ? UitkTextInput : UitkTypeaheadInput;
        if (customTrigger) {
            return children;
        }
        return (React.createElement("div", null,
            mounted && (React.createElement(UitkFakeInput, { "aria-label": [a11yLabel, searchTerm].filter(function (str) { return str && str.length > 0; }).join(' '), "aria-expanded": dialog, icon: icon, label: label, hasClearButton: hasClearButton, handleClear: this.handleClear, "aria-describedby": invalid && stid ? "".concat(stid, "-input-error") : undefined, "data-stid": stid && triggerType === TypeaheadRenderType.MENU
                    ? "".concat(stid, "-menu-trigger")
                    : stid && triggerType === 'dialog'
                        ? "".concat(stid, "-dialog-trigger")
                        : '', domRef: this.typeaheadInputRef, onClick: function () {
                    _this.openTypeahead(triggerType);
                }, onKeyPress: function (e) {
                    _this.handleKeyPress(e, triggerType);
                }, onClickCapture: analytics && analyticsTracker.bind(this, analytics, ''), placeholder: placeholder, value: isMulti ? value.join(', ') : value, invalid: invalid, required: required })),
            React.createElement(TriggerTag, { className: getClassNameList({
                    'is-hidden': mounted
                }), icon: icon, label: label, "data-stid": stid && "".concat(stid, "-input"), domRef: this.typeaheadInputRef, hasClearButton: false, hasVisibleLabel: hasVisibleLabel, name: name, onChange: Typeahead.defaultProps.onInputChange, placeholder: placeholder, value: searchTerm, id: stid && "".concat(stid, "-input"), required: required })));
    };
    Typeahead.prototype.renderMenuTypeahead = function () {
        var _a = this.props, renderMenu = _a.renderMenu, customTrigger = _a.customTrigger;
        var dialog = this.state.dialog;
        var showTypeahead = (customTrigger && customTrigger.showTypeahead) || dialog;
        return renderMenu ? (renderMenu(this.renderMenu())) : (React.createElement(React.Fragment, null,
            React.createElement("div", { className: getClassNameList('uitk-typeahead-menu-layer', {
                    'uitk-typeahead-menu-layer-responsive': showTypeahead,
                    'is-hidden': !showTypeahead
                }) }, this.renderMenu()),
            this.renderTrigger(TypeaheadRenderType.MENU)));
    };
    Typeahead.prototype.renderDialogTypeahead = function () {
        var _a = this.props, renderDialog = _a.renderDialog, customTrigger = _a.customTrigger;
        var dialog = this.state.dialog;
        var showTypeahead = (customTrigger && customTrigger.showTypeahead) || dialog;
        return (React.createElement(React.Fragment, null,
            renderDialog ? (dialog ? (renderDialog(this.renderDialog())) : null) : (React.createElement("div", { className: getClassNameList('uitk-typeahead-dialog-layer', {
                    'uitk-typeahead-dialog-layer-responsive': showTypeahead,
                    'is-hidden': !showTypeahead
                }) }, this.renderDialog())),
            this.renderTrigger(TypeaheadRenderType.DIALOG)));
    };
    Typeahead.prototype.render = function () {
        var _a = this.props, renderType = _a.renderType, stid = _a.stid, isMulti = _a.isMulti, value = _a.value, onItemRemove = _a.onItemRemove, clearAllText = _a.clearAllText, showPills = _a.showPills, label = _a.label;
        return (React.createElement("div", { className: getClassNameList('uitk-typeahead'), "data-stid": stid },
            renderType === TypeaheadRenderType.DIALOG ? (this.renderDialogTypeahead()) : renderType === TypeaheadRenderType.MENU ? (this.renderMenuTypeahead()) : renderType === TypeaheadRenderType.ADAPTIVE ? (React.createElement(Viewport, null,
                React.createElement(ViewSmall, null, this.renderDialogTypeahead()),
                React.createElement(ViewLarge, null, this.renderMenuTypeahead()))) : (''),
            isMulti && showPills ? (React.createElement(UitkTypeaheadSelections, { typeaheadLabel: label, hideHeading: true, values: value, stid: stid, onItemRemove: onItemRemove, onClear: this.handleClear, clearAllText: clearAllText, className: getClassNameList('uitk-spacing-margin-blockstart-three') })) : null));
    };
    Typeahead.displayName = 'UitkTypeahead';
    Typeahead.defaultProps = {
        hasClearButton: false,
        onInputChange: function () { return null; },
        renderType: TypeaheadRenderType.ADAPTIVE,
        isMulti: false
    };
    return Typeahead;
}(React.Component));
var UitkTypeahead = React.forwardRef(function (props, ref) {
    var l10n = useLocalization();
    return React.createElement(Typeahead, __assign({ l10n: l10n, ref: ref }, props));
});

export { TypeaheadRenderType, UitkTypeahead };
